/**
 * Where a cloud run's skills come from (YAG-784), in the words every surface
 * uses: the per-repository switch (Connections → Repositories, Settings,
 * Agent runs, and the Team edit sheet; on by default since YAG-1006), the
 * workspace's plugin marketplace, a person's own skills repository, and the
 * Receipt's "Skills loaded" row.
 *
 * One place so the copy stays the same everywhere and a test can hold all of
 * it to the house rules (no em-dashes, no internal nouns, never "trust").
 */
import type { AgentSourceRecord } from "@yagni/shared";

// ── The per-repository switch ───────────────────────────────────────────────

export const AGENT_TRUST_TITLE = "Agents use this repository's skills and rules";
export const AGENT_TRUST_COPY =
  "On by default: Wright loads the skills, commands, rules and plugins committed here when it builds. Switch off to hide them; a build then reads only AGENTS.md and CLAUDE.md.";
export const AGENT_TRUST_READ_ONLY =
  "Workspace admins and the owner of a Team that works on it change this.";
export const AGENT_TRUST_FAILED = "The change was not saved.";
export const AGENT_TRUST_LOAD_FAILED =
  "Whether this repository's skills load could not be read just now.";

// ── The workspace's plugin marketplace ──────────────────────────────────────

export const MARKETPLACE_SECTION = "Plugin marketplace";
export const MARKETPLACE_INPUT = "Plugin marketplace repository";
export const MARKETPLACE_PLACEHOLDER = "acme/claude-plugins";
export const MARKETPLACE_HELP = "Loaded fresh at the start of every run.";
export const MARKETPLACE_CHECK = "Check";
export const MARKETPLACE_SAVE = "Save";
export const MARKETPLACE_REMOVE = "Remove";
export const MARKETPLACE_UNSUPPORTED = "Not supported yet";
export const MARKETPLACE_NO_PLUGINS = "This marketplace lists no plugins.";
export const MARKETPLACE_NONE = "No plugin marketplace set.";
export const MARKETPLACE_ADMINS_ONLY = "Workspace admins change this.";
export const MARKETPLACE_SAVED = "Saved. The next run loads these plugins.";
export const MARKETPLACE_REMOVED = "Removed. Runs load no marketplace plugins.";
export const MARKETPLACE_FAILED = "The plugin marketplace was not saved.";
export const MARKETPLACE_CHECK_FAILED = "The marketplace could not be read just now.";
export const MARKETPLACE_ENABLED = (plugins: readonly string[]) =>
  plugins.length > 0 ? `Enabled: ${plugins.join(", ")}` : "No plugins enabled.";

// ── A person's own skills repository ────────────────────────────────────────

export const SKILLS_SECTION = "Your skills";
export const SKILLS_INPUT = "Your skills repository";
export const SKILLS_PLACEHOLDER = "owner/skills";
export const SKILLS_HELP =
  "Pick one of this workspace's connected repositories. Used only on runs you start and on your personal Team.";
export const SKILLS_SAVE = "Save";
export const SKILLS_REMOVE = "Remove";
export const SKILLS_SAVED = (sha: string) => `Saved. Latest commit ${sha.slice(0, 7)}.`;
export const SKILLS_REMOVED = "Removed.";
export const SKILLS_FAILED = "Your skills repository was not saved.";

/** Said before a request goes out when the input isn't a GitHub repository. */
export const GITHUB_REPO_HINT = "Enter a GitHub repository as owner/name or its github.com link.";

// ── The Receipt ─────────────────────────────────────────────────────────────

export const SKILLS_LOADED_LABEL = "Skills loaded";
export const REPO_SKILLS_OFF = "skills and rules off";

function shortSha(sha: string | null): string | null {
  return sha ? sha.slice(0, 7) : null;
}

/**
 * One source as the Receipt says it:
 *
 *   acme/app @ 1a2b3c4 (this repository)
 *   acme/plugins @ 9f8e7d6 · review-kit, db-tools
 *   jane/skills @ 0a1b2c3 (personal)
 *   acme/plugins · not loaded: <reason>
 *   acme/app · skills and rules off
 *
 * The last is the `untrusted` status. Since YAG-1006 it means an owner
 * switched the repository's skills off; on a run from before, it also meant
 * nobody had switched them on. "Off" is true of both, so old Receipts read
 * right without a rewrite.
 */
export function agentSourceLine(record: AgentSourceRecord): string {
  if (record.status === "untrusted") return `${record.fullName} · ${REPO_SKILLS_OFF}`;
  if (record.status === "unavailable") {
    const reason = record.reason?.trim();
    return reason
      ? `${record.fullName} · not loaded: ${reason}`
      : `${record.fullName} · not loaded`;
  }
  const sha = shortSha(record.sha);
  const at = sha ? `${record.fullName} @ ${sha}` : record.fullName;
  if (record.kind === "repo") return `${at} (this repository)`;
  if (record.kind === "personal") return `${at} (personal)`;
  const plugins = record.enabledPlugins ?? [];
  return plugins.length > 0 ? `${at} · ${plugins.join(", ")}` : at;
}
